import { forwardRef, useCallback, type MouseEvent } from "react";
import { Button, ButtonProps } from "../../../components/Button";
import { usePopoverContext } from "./internal/usePopoverContext";
import { childrenToString } from "../../../internal/functions";
import { DataTrackingId } from "../../../types";
import { useTrackingId } from "../../../hooks";

/**
 * Props for the PopoverClose component.
 * @extends ButtonProps
 */
export type PopoverCloseProps = ButtonProps & DataTrackingId;

/**
 * PopoverClose component for closing the popover.
 *
 * Features:
 * - Supports all Button component props and styling
 * - Closes the popover on click via closePopover
 * - Automatic tracking ID generation for analytics
 *
 * @example
 * <Popover.Close>Close</Popover.Close>
 *
 * @example
 * <Popover.Close appearance="secondary" size="small">
 *   Cancel
 * </Popover.Close>
 */
export const PopoverClose = forwardRef<HTMLButtonElement, PopoverCloseProps>(
  function PopoverClose(props, ref) {
    const { onClick, ...rest } = props;
    const { closePopover } = usePopoverContext();

    const trackingId = useTrackingId({
      name: "PopoverClose",
      data: { children: childrenToString(props.children) },
      hasOverride: !!props["data-tracking-id"],
    });

    const handleClick = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        closePopover();
        onClick?.(event);
      },
      [closePopover, onClick],
    );

    return (
      <Button
        ref={ref}
        data-tracking-id={trackingId}
        data-anv="popover-close"
        {...rest}
        onClick={handleClick}
      />
    );
  },
);

PopoverClose.displayName = "PopoverClose";
